import { filterDOMProps, useSlotId } from '@react-aria/utils';
import type { AriaLabelingProps, DOMProps } from '@react-types/shared';
import type { ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';
import { Provider } from 'react-aria-components';

import { IconContext } from '@infinex/ui-icons/context';
import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useStyleProps } from '@infinex/ui-styles';

import { ButtonContext, LinkButtonContext } from '../button';
import { ContentContext, FooterContext, HeadingContext } from '../content';

export type EmptyStateProps = {
  /**
   * The content of the empty state.
   */
  children: ReactNode;
  /**
   * The size of the empty state. Influences typographic elements, and any
   * buttons provided.
   *
   * @default 'medium'
   */
  size?: 'small' | 'medium';
} & DOMProps &
  AriaLabelingProps &
  BaseStyleProps &
  UnsafeStyleProps;

/**
 * Empty states are used as placeholders when content is not yet available, or
 * is temporarily absent due to the state of the view.
 *
 * [Taurus docs: `EmptyState`](https://ui-sb.app.infinex.dev/?path=/docs/components-emptystate--docs)
 */
export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  function EmptyState(props, forwardedRef) {
    const { children, size = 'medium', ...otherProps } = props;

    const slotClasses = useMemo(() => getSlotClasses(size), [size]);
    const { className, style } = useStyleProps(props);
    let headingId: string | undefined = useSlotId();
    headingId = props['aria-label'] ? undefined : headingId;

    return (
      <section
        {...filterDOMProps(otherProps, { labelable: true })}
        aria-labelledby={props['aria-labelledby'] ?? headingId}
        ref={forwardedRef}
        style={style}
        className={cn(
          'text-default flex min-h-20 flex-col items-center justify-center text-center',
          {
            'gap-4 p-4 text-sm': size === 'small',
            'gap-6 p-6 text-base': size === 'medium',
          },
          className
        )}
      >
        <Provider
          values={[
            [HeadingContext, { id: headingId, className: slotClasses.heading }],
            [IconContext, { className: slotClasses.icon }],
            [ContentContext, { className: slotClasses.content }],
            [FooterContext, { className: slotClasses.footer }],
            [ButtonContext, { size }],
            [LinkButtonContext, { size }],
          ]}
        >
          {children}
        </Provider>
      </section>
    );
  }
);

function getSlotClasses(size: EmptyStateProps['size']) {
  return {
    icon: cn('fill-emphasis', {
      'size-8': size === 'medium',
      'size-6': size === 'small',
    }),
    heading: cn('text-emphasis', {
      'text-xl font-semibold': size === 'medium',
      'text-base font-semibold': size === 'small',
    }),
    content: cn('flex flex-col items-center gap-2 text-pretty', {
      'max-w-md': size === 'small',
      'max-w-lg': size === 'medium',
    }),
    footer: cn(
      'xs:items-center xs:justify-center xs:flex-row flex flex-col self-stretch',
      {
        'gap-2': size === 'small',
        'gap-3': size === 'medium',
      }
    ),
  };
}
